import { Button, CircularProgress, Typography } from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import { Alert } from "@material-ui/lab";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import axios from "../../axiosCF";
import PendingFiles from "../../components/PendingFiles/PendingFiles";
import CustomDialog from "../../components/UI/CustomDialog/CustomDialog";
import Input, { EInputType } from "../../components/UI/Input/Input";
import {
  UNION_TARGET_GROUPS,
  unionEducationContentOptions,
  unionTargetGroupOptions,
} from "../../data/union";
import withErrorHandler from "../../hoc/withErrorHandler/withErrorHandler";
import { IUser } from "../../interfaces/domain";
import { IUnion } from "../../interfaces/domain/IUnion";
import { IAppState } from "../../interfaces/state";
import {
  defaultInputChangedHandler,
  getFormData,
  getInvalidControlLabels,
  IControls,
  IInputConfigOptional,
  touchAllControls,
  validateInput,
} from "../../shared/utility";
import * as actions from "../../store/actions";

import classes from "./UnionEducation.module.scss";

export enum EFields {
  union = "union",
  unionOther = "unionOther",
  name = "name",
  educationDate = "educationDate",
  targetGroups = "targetGroups",
  targetGroupAthleteInfo = "targetGroupAthleteInfo",
  targetGroupOther = "targetGroupOther",
  participantsAmount = "participantsAmount",
  instructor = "instructor",
  instructorEmail = "instructorEmail",
  educationContents = "educationContents",
  educationContentsOther = "educationContentsOther",
  educationContentDescription = "educationContentDescription",
  comments = "comments",
  attachments = "attachments",
  implementationAndMaterials = "implementationAndMaterials",
  infoSession = "infoSession",
}

const initControls: IControls = {
  controls: {
    [EFields.union]: {
      type: EInputType.select,
      value: "",
      label: "Urheilujärjestö",
      options: [],
      validation: {
        required: true,
        or: ["unionOther"],
      },
      valid: false,
    },
    [EFields.unionOther]: {
      type: EInputType.text,
      value: "",
      label: "Muu urheilujärjestö",
      validation: {
        required: true,
        or: ["union"],
      },
      valid: false,
    },
    [EFields.name]: {
      type: EInputType.text,
      value: "",
      label: "Koulutuksen nimi",
      validation: {
        required: true,
      },
      valid: false,
    },
    [EFields.educationDate]: {
      type: EInputType.date,
      value: null,
      label: "Päivämäärä",
      validation: {
        required: true,
      },
      valid: false,
    },
    [EFields.targetGroups]: {
      type: EInputType.select,
      value: [],
      label: "Kohderyhmät",
      options: unionTargetGroupOptions,
      multiple: true,
      validation: {
        required: true,
        or: ["targetGroupOther"],
      },
      valid: false,
    },
    [EFields.targetGroupAthleteInfo]: {
      type: EInputType.text,
      value: "",
      label: "Urheilijat kohderyhmän tarkennus",
      validation: {
        required: false,
      },
      valid: true,
    },
    [EFields.targetGroupOther]: {
      type: EInputType.text,
      value: "",
      label: "Muu kohderyhmä",
      validation: {
        required: true,
        or: ["targetGroups"],
      },
      valid: false,
    },
    [EFields.participantsAmount]: {
      type: EInputType.number,
      value: "",
      label: "Osallistujamäärä",
      validation: {
        required: true,
      },
      valid: false,
    },
    [EFields.instructor]: {
      type: EInputType.text,
      value: "",
      label: "Kouluttaja",
      validation: {
        required: true,
      },
      valid: false,
    },
    [EFields.instructorEmail]: {
      type: EInputType.email,
      value: "",
      label: "Sähköposti",
      validation: {
        required: true,
        email: true,
      },
      valid: false,
    },
    [EFields.educationContents]: {
      type: EInputType.select,
      value: [],
      label: "Koulutuksen sisältö",
      options: unionEducationContentOptions,
      multiple: true,
      validation: {
        required: true,
        or: ["educationContentsOther"],
      },
      valid: false,
    },
    [EFields.educationContentsOther]: {
      type: EInputType.text,
      value: "",
      label: "Muu koulutuksen sisältö",
      validation: {
        required: true,
        or: ["educationContents"],
      },
      valid: false,
    },
    [EFields.educationContentDescription]: {
      type: EInputType.text,
      value: "",
      label: "Kuvaus koulutuksen sisällöstä",
      multiline: true,
      validation: {
        required: false,
      },
      valid: true,
    },
    [EFields.comments]: {
      type: EInputType.text,
      value: "",
      label:
        "Muita kommentteja koulutuksesta (esim. osallistujapalaute) tai toiveita SUEKille",
      multiline: true,
      validation: {
        required: false,
      },
      valid: true,
    },
    [EFields.attachments]: {
      type: EInputType.dropzone,
      value: "",
      label: "Liitteet",
      validation: {
        required: true,
        or: [EFields.implementationAndMaterials],
      },
      valid: false,
    },
    [EFields.implementationAndMaterials]: {
      type: EInputType.text,
      value: "",
      label: "Kuvaa tarkasti koulutuksen toteutustapa ja käytettyjä materiaaleja",
      multiline: true,
      validation: {
        required: true,
        or: [EFields.attachments],
      },
      valid: false,
    },
    [EFields.infoSession]: {
      type: EInputType.checkbox,
      value: [],
      options: [{ value: "Y", text: "Tietoisku n.15 min" }],
      label: "Tietoisku n.15 min",
      validation: {
        required: false
      },
      valid: true,
    },
  },
  isValid: false,
};

interface IProps {}

const UnionEducation: React.FC<IProps> = () => {
  const [controls, setControls] = useState(initControls);
  const [isOpen, setOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const dispatch: any = useDispatch();
  const { unions, unionsLoading, user, unionEducation, loading } = useSelector(
    (state: IAppState) => ({
      loading: state.union.loading,
      unionEducation: state.union.unionEducation,
      unions: state.union.unions,
      unionsLoading: state.union.unionsLoading,
      user: state.auth.user,
    }),
  );

  const applyUserDefaults = useCallback(
    (baseControls: IControls, currentUser: IUser | null): IControls => {
      if (!currentUser) {
        return baseControls;
      }
      let updatedControls = baseControls;
      if (currentUser.name) {
        updatedControls = validateInput(
          updatedControls.controls,
          EFields.instructor,
          currentUser.name,
          false,
        );
      }
      if (currentUser.email) {
        updatedControls = validateInput(
          updatedControls.controls,
          EFields.instructorEmail,
          currentUser.email,
          false,
        );
      }
      return updatedControls;
    },
    [],
  );

  useEffect(() => {
    dispatch(actions.getUnions());
  }, [dispatch]);

  useEffect(() => {
    if (user !== null) {
      setControls((prevControls) => applyUserDefaults(prevControls, user));
    }
  }, [user, applyUserDefaults]);

  const populateUnionOptions = useCallback((unionList: IUnion[] | null) => {
    if (unionList !== null) {
      setControls((prevControls) => {
        return {
          ...prevControls,
          controls: {
            ...prevControls.controls,
            [EFields.union]: {
              ...prevControls.controls[EFields.union],
              options: unionList.map((union) => ({
                text: union.name,
                value: union.id,
              })),
            },
          },
        };
      });
    }
  }, []);

  useEffect(() => {
    populateUnionOptions(unions);
  }, [unions, populateUnionOptions]);

  useEffect(() => {
    if (unionEducation && unionEducation.id) {
      setOpen(true);
    }
  }, [unionEducation]);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!controls.isValid) {
      setSubmitAttempted(true);
      setControls((prevControls) => touchAllControls(prevControls.controls));
      return;
    }

    const controlsData = getFormData(controls.controls);

    const formData = new FormData();
    if (pendingFiles && pendingFiles.length > 0) {
      pendingFiles.forEach((file, index) => {
        formData.append(`file_${index + 1}`, file);
      });
      formData.append("fileAmount", pendingFiles.length.toString());
    }
    formData.append("data", JSON.stringify(controlsData));

    dispatch(actions.saveUnionEducation(formData));
  };

  const onResetForm = () => {
    setSubmitAttempted(false);
    setControls(applyUserDefaults(initControls, user));
    setPendingFiles([]);
    populateUnionOptions(unions);
  };

  const onCloseDialog = () => {
    setOpen(false);
    onResetForm();
  };

  const createInput = (inputName: EFields, config?: IInputConfigOptional) => {
    return (
      <Input
        formControl={{
          id: inputName,
          config: {
            ...controls.controls[inputName],
            ...config,
          },
        }}
        changed={(value, controlName: string) =>
          defaultInputChangedHandler(value, controlName, setControls)
        }
      />
    );
  };

  useEffect(() => {
    const attachmentsValue = pendingFiles.length > 0 ? "has_files" : "";
    setControls((prevControls) => {
      if (prevControls.controls[EFields.attachments].value === attachmentsValue) {
        return prevControls;
      }
      return validateInput(
        prevControls.controls,
        EFields.attachments,
        attachmentsValue,
        false,
      );
    });
  }, [pendingFiles]);

  const dropHandler = useCallback((files: File[]) => {
    setPendingFiles((prevFiles) => {
      const uniqueNewFiles = files.filter(
        (file, idx, self) =>
          !prevFiles.some(
            (existing) =>
              existing.name === file.name && existing.size === file.size,
          ) &&
          self.findIndex(
            (f) => f.name === file.name && f.size === file.size,
          ) === idx,
      );
      return [...prevFiles, ...uniqueNewFiles];
    });
  }, []);

  const removePendingFileHandler = useCallback((index: number) => {
    setPendingFiles((prevFiles) => {
      const newFiles = [...prevFiles];
      newFiles.splice(index, 1);
      return newFiles;
    });
  }, []);

  const hasAthleteSelected = (
    controls.controls[EFields.targetGroups].value as string[]
  ).includes(UNION_TARGET_GROUPS.ATHLETES);

  const invalidLabels =
    submitAttempted && !controls.isValid
      ? getInvalidControlLabels(controls.controls)
      : [];

  return (
    <React.Fragment>
      <CustomDialog
        title="Kiitos"
        content="Olemme vastaanottaneet koulutuksen tiedot, kiitos."
        buttonText="Sulje"
        onOk={onCloseDialog}
        open={isOpen}
      />
      <Paper elevation={4} className={classes.Paper}>
        <Typography variant="h5" gutterBottom>
          Lajiliittojen järjestämät koulutukset
        </Typography>
        <Typography variant="subtitle1">
          Tällä lomakkeella voit ilmoittaa lajiliittosi toteuttamat antidoping-
          ja kilpailumanipulaatiokoulutukset. Huomioithan, että SUEKilta
          tilattuja koulutuksia ei raportoida tämän lomakkeen kautta. Tähdellä
          (*) merkityt kentät ovat pakollisia.
        </Typography>
        {unionsLoading || loading ? (
          <CircularProgress />
        ) : (
          <form onSubmit={onSubmit} noValidate>
            {createInput(EFields.union)}
            {createInput(EFields.unionOther)}
            {createInput(EFields.name)}
            {createInput(EFields.educationDate)}
            {createInput(EFields.targetGroups)}
            {hasAthleteSelected && createInput(EFields.targetGroupAthleteInfo)}
            {createInput(EFields.targetGroupOther)}
            {createInput(EFields.participantsAmount)}
            {createInput(EFields.instructor)}
            {createInput(EFields.instructorEmail)}
            {createInput(EFields.educationContents)}
            {createInput(EFields.educationContentsOther)}
            {createInput(EFields.educationContentDescription)}
            {createInput(EFields.infoSession)}
            {createInput(EFields.comments)}

            <p>
              Lataa aina käyttämäsi koulutusmateriaali tai mikäli et käyttänyt diaesitystä, kuvaa alle tarkasti koulutuksen toteutustapa
            </p>
            {createInput(EFields.attachments, { onDropHandler: dropHandler })}
            <PendingFiles
              pendingFiles={pendingFiles}
              onRemove={removePendingFileHandler}
            />
            {createInput(EFields.implementationAndMaterials)}

            {submitAttempted && !controls.isValid && invalidLabels.length > 0 && (
              <Alert severity="error" className={classes.Alert}>
                Täytä tai tarkista vielä seuraavat pakolliset kentät:{" "}
                <strong>{invalidLabels.join(", ")}</strong>
              </Alert>
            )}

            <Button
              color="primary"
              className={classes.Button}
              type="submit"
            >
              Lähetä
            </Button>

            <Button className={classes.Button} onClick={onResetForm}>
              Tyhjennä
            </Button>
          </form>
        )}
      </Paper>
    </React.Fragment>
  );
};

export default withErrorHandler<IProps>(UnionEducation, axios);
